import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { getName } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { TranscriptorConsultation } from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import { dentalConsultationMessages } from '../config';
import { getPersonFullName } from '../lib';

export const useConsultationAuthorName = (
  consultation?: TranscriptorConsultation | null,
): string => {
  const { formatMessage } = useIntl();
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const createdBy = consultation?.createdBy ?? '';
  const authorSelector = useMemo(
    () => organizationModel.selectors.selectUserByID(createdBy),
    [createdBy],
  );
  const orgAuthor = useAppSelector(authorSelector);

  return useMemo(() => {
    const staffName = getPersonFullName(consultation?.staffInfo);

    if (staffName) {
      return staffName;
    }

    const accountName = consultation?.authorUser?.name?.trim();

    if (accountName) {
      return accountName;
    }

    const orgAuthorName = orgAuthor
      ? getName(
          orgAuthor.PersonalData?.FirstName,
          orgAuthor.PersonalData?.LastName,
          { userLocale },
        ).trim()
      : '';

    if (orgAuthorName) {
      return orgAuthorName;
    }

    return formatMessage(dentalConsultationMessages.doctorDefaultName);
  }, [
    consultation?.staffInfo,
    consultation?.authorUser,
    orgAuthor,
    userLocale,
    formatMessage,
  ]);
};
